import * as React from 'react'
import { Avatar as AvatarPrimitive } from '@base-ui/react/avatar'

import { getStripedBackground } from '@campai/lib-common/utils/misc'

import { cn } from '@/components/new/lib/utils'

export type AvatarSizeType = 'default' | 'xs' | 'sm' | 'lg' | 'xl'

function Avatar({
    className,
    size = 'default',
    square = false,
    ...props
}: AvatarPrimitive.Root.Props & {
    size?: AvatarSizeType
    square?: boolean
}) {
    return (
        <AvatarPrimitive.Root
            data-slot="avatar"
            data-size={size}
            data-square={square ? 'true' : 'false'}
            className={cn(
                'after:border-border group/avatar relative flex size-8 shrink-0 select-none rounded-full after:absolute after:inset-0 after:rounded-full after:border after:mix-blend-darken data-[size=lg]:size-10 data-[size=xs]:size-[18px] data-[size=sm]:size-[22px] data-[size=xl]:size-14 data-[square=true]:rounded-lg data-[square=true]:after:rounded-lg dark:after:mix-blend-lighten',
                className
            )}
            {...props}
        />
    )
}

function AvatarImage({ className, ...props }: AvatarPrimitive.Image.Props) {
    return (
        <AvatarPrimitive.Image
            data-slot="avatar-image"
            className={cn(
                'aspect-square size-full rounded-full object-cover group-data-[square=true]/avatar:rounded-lg',
                className
            )}
            {...props}
        />
    )
}

function AvatarFallback({ className, children, ...props }: AvatarPrimitive.Fallback.Props) {
    return (
        <AvatarPrimitive.Fallback
            data-slot="avatar-fallback"
            className={cn(
                'bg-muted text-muted-foreground flex size-full items-center justify-center rounded-full text-sm group-data-[square=true]/avatar:rounded-lg group-data-[size=xs]/avatar:text-[8px] group-data-[size=sm]/avatar:text-[10px] group-data-[size=lg]/avatar:text-base group-data-[size=xl]/avatar:text-xl',
                className
            )}
            {...props}
        >
            {children}
        </AvatarPrimitive.Fallback>
    )
}

// Seeded stripes for an avatar fallback without an image, matching the Connect app's placeholder for the same seed
function AvatarStripes({ seed, className, ...props }: React.ComponentProps<'svg'> & { seed: string }) {
    const { backgroundColor, stripeColor, rotation, stripes } = getStripedBackground(seed)

    const pattern = (
        <g fill={stripeColor} transform={`rotate(${rotation}, 50, 50)`}>
            {stripes.map((stripe, index) => (
                <rect key={index} {...stripe} />
            ))}
        </g>
    )

    return (
        <svg
            data-slot="avatar-stripes"
            aria-hidden
            viewBox="0 0 100 100"
            preserveAspectRatio="xMidYMid slice"
            className={cn('size-full rounded-[inherit]', className)}
            {...props}
        >
            <rect x={-50} y={-50} width={200} height={200} fill={backgroundColor} />
            <g style={{ mixBlendMode: 'overlay' }}>{pattern}</g>
            <g opacity={0.3}>{pattern}</g>
        </svg>
    )
}

function AvatarBadge({ className, ...props }: React.ComponentProps<'span'>) {
    return (
        <span
            data-slot="avatar-badge"
            className={cn(
                'bg-primary text-primary-foreground ring-background absolute bottom-0 right-0 z-10 inline-flex select-none items-center justify-center rounded-full bg-blend-color ring-2',
                'group-data-[size=xs]/avatar:size-1.5 group-data-[size=xs]/avatar:[&>svg]:hidden',
                'group-data-[size=sm]/avatar:size-2 group-data-[size=sm]/avatar:[&>svg]:hidden',
                'group-data-[size=default]/avatar:size-2.5 group-data-[size=default]/avatar:[&>svg]:size-2',
                'group-data-[size=lg]/avatar:size-3 group-data-[size=lg]/avatar:[&>svg]:size-2',
                'group-data-[size=xl]/avatar:size-4 group-data-[size=xl]/avatar:[&>svg]:size-3',
                className
            )}
            {...props}
        />
    )
}

function AvatarGroup({ className, ...props }: React.ComponentProps<'div'>) {
    return (
        <div
            data-slot="avatar-group"
            className={cn(
                '*:data-[slot=avatar]:ring-background group/avatar-group flex -space-x-2 *:data-[slot=avatar]:ring-2',
                className
            )}
            {...props}
        />
    )
}

function AvatarGroupCount({ className, ...props }: React.ComponentProps<'div'>) {
    return (
        <div
            data-slot="avatar-group-count"
            className={cn(
                'bg-muted text-muted-foreground group-has-data-[size=lg]/avatar-group:size-10 group-has-data-[size=xs]/avatar-group:size-[18px] group-has-data-[size=xs]/avatar-group:text-xs group-has-data-[size=xs]/avatar-group:[&>svg]:size-2.5 group-has-data-[size=sm]/avatar-group:size-[22px] group-has-data-[size=xl]/avatar-group:size-14 group-has-data-[size=xl]/avatar-group:text-base group-has-data-[size=lg]/avatar-group:[&>svg]:size-5 group-has-data-[size=sm]/avatar-group:[&>svg]:size-3 group-has-data-[size=xl]/avatar-group:[&>svg]:size-7 ring-background relative flex size-8 shrink-0 items-center justify-center rounded-full text-sm ring-2 [&>svg]:size-4',
                className
            )}
            {...props}
        />
    )
}

export { Avatar, AvatarImage, AvatarFallback, AvatarStripes, AvatarGroup, AvatarGroupCount, AvatarBadge }
